import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js';
import LocalizedFormat from 'dayjs/plugin/localizedFormat.js';
import QuarterOfYear from 'dayjs/plugin/quarterOfYear.js';
import RelativeTime from 'dayjs/plugin/relativeTime.js';
import utc from 'dayjs/plugin/utc.js';

dayjs.extend(RelativeTime);
dayjs.extend(LocalizedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
dayjs.extend(QuarterOfYear);

export const DEFAULT_DATE_FORMAT = 'LL';
export const MAX_DATE_VALUE = '9999-12-31';

export const formatDate = (date?: string | Date | null, format?: string) =>
  date ? dayjs(date).format(format || DEFAULT_DATE_FORMAT) : '';

export const formatDateUtc = (date?: string | Date | null, format?: string) =>
  date ? dayjs.utc(date).format(format || DEFAULT_DATE_FORMAT) : '';

export const formatCommentedTime = (value: string) => {
  const time = dayjs(value).fromNow();

  if (parseInt(time.split(' ')[0] || '0') > 9 && time.split(' ')[1] === 'days') {
    return formatDate(value);
  }

  return time;
};

export function getToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/** The user's calendar date, the same one the date inputs and dateToMidnight speak. */
export function getTodayDateString() {
  return dayjs().format('YYYY-MM-DD');
}

export function isPastDate(date: string) {
  return new Date(date).getTime() <= Date.now();
}

/**
 * return the date string from datepicker(YYYY-MM-DD) to midnight in the user's timezone
 */
export function dateToMidnight(inputValue: string) {
  if (inputValue.includes('T')) {
    const date = new Date(inputValue);
    date.setHours(0, 0, 0, 0);
    return date;
  }
  // Parse YYYY-MM-DD via the numeric Date constructor, not Date.parse.
  // `Date.parse("YYYY-MM-DD 00:00:00")` (space separator) falls into the V8
  // legacy parser, which mangles low years: e.g. "0020-05-04 00:00:00" parses
  // as Feb 4 2004, not May 4 year 20. The numeric constructor preserves the
  // year literally.
  const [y, m, d] = inputValue.split('-').map(Number);
  if (y === undefined) {
    return new Date(NaN);
  }
  return new Date(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
}

/**
 * Set the date to midnight in the user's timezone
 * and convert it to UTC in ISO format.
 */
export function dateToMidnightUTC(inputValue: string | undefined): string {
  if (!inputValue) {
    return '';
  }

  try {
    return dateToMidnight(inputValue).toISOString();
  } catch {
    return '';
  }
}

export { dayjs };
